import { enableMapSet } from "immer";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import type { ChatMessage } from "@/sdk";

type ChatId = string;
type MessageId = string;

interface ChatState {
    readonly activeChatId: ChatId | undefined;
    readonly typingCustomerChatIds: Set<ChatId>;
    setActiveChatId: (chatId: ChatId) => void;
    clearActiveChatId: () => void;
    /** used to persist inputs while switching between ChatId */
    readonly inputs: Map<ChatId, string | undefined>;
    setInput: (chatId: ChatId, input: string | undefined) => void;
    isCustomerTyping: (chatId: ChatId) => boolean;
}

interface ChatMessagesState extends ChatMessagesCache {
    /** map of ChatId to messageId to message */
    readonly chats: Map<ChatId, Map<MessageId, ChatMessage>>;
}

/**
 * Add **only actions** that are directly used by `TwilioChatSdk` to `ChatMessagesCache`.
 * Do not include any state in this type.
 * If you need to add actions not used directly by `TwilioChatSdk` or any state,
 * consider using `ChatState` or `ChatMessagesState` instead.
 */
export interface ChatMessagesCache {
    addChatMessages: (chatId: ChatId, messages: ChatMessage[]) => void;
    removeChatMessages: (chatId: ChatId) => void;
    /**
     * @example
     * // Example 1: Update all messages in a specific chat
     * updateChatMessages(
     *   (chatId, _message) => chatId === 'CHxxxxxx',
     *   (message) => ({ ...message, delivered: true })
     * );
     *
     * @example
     * // Example 2: Update only system messages
     * updateChatMessages(
     *   (_chatId, message) => message.senderType === 'system',
     *   (message) => ({ ...message, showTo: 'expert' })
     * );
     *
     * @example
     * // Example 3: Update only customer messages in a specific chat
     * updateChatMessages(
     *   (chatId, message) => chatId === 'CHxxxxxx' && message.senderType === 'customer',
     *   (message) => ({ ...message, readByOtherParticipant: true })
     * );
     */
    updateChatMessages: (
        shouldUpdate: (chatId: ChatId, message: ChatMessage) => boolean,
        update: (message: ChatMessage) => ChatMessage,
    ) => void;

    markCustomerIsTyping: (chatId: ChatId) => void;
    markCustomerStoppedTyping: (chatId: ChatId) => void;
}

enableMapSet();
export const useChatStore = create<ChatMessagesState & ChatState>()(
    immer(
        devtools(
            (set, get) => ({
                activeChatId: undefined,
                typingCustomerChatIds: new Set<ChatId>(),
                markCustomerIsTyping: (chatId: ChatId): void => {
                    set(
                        (state) => {
                            state.typingCustomerChatIds.add(chatId);
                        },
                        false,
                        "markCustomerIsTyping",
                    );
                },
                markCustomerStoppedTyping: (chatId: ChatId): void => {
                    set(
                        (state) => {
                            state.typingCustomerChatIds.delete(chatId);
                        },
                        false,
                        "markCustomerStoppedTyping",
                    );
                },
                isCustomerTyping: (chatId: ChatId): boolean => {
                    return get().typingCustomerChatIds.has(chatId);
                },
                setActiveChatId: (chatId: ChatId): void =>
                    set(
                        {
                            activeChatId: chatId,
                        },
                        false,
                        "setActiveChatId to " + chatId,
                    ),
                clearActiveChatId: (): void =>
                    set(
                        {
                            activeChatId: undefined,
                        },
                        false,
                        "clearActiveChatId",
                    ),
                chats: new Map<ChatId, Map<MessageId, ChatMessage>>(),
                addChatMessages: (chatId: ChatId, newMessages: ChatMessage[]): void =>
                    set(
                        (state) => {
                            const messages = state.chats.get(chatId) ?? new Map<string, ChatMessage>();
                            newMessages.forEach((newMessage) => messages.set(newMessage.id, newMessage));
                            state.chats.set(chatId, messages);
                        },
                        false,
                        "addChatMessages",
                    ),
                removeChatMessages: (chatId: ChatId): void =>
                    set(
                        (state) => {
                            void state.chats.delete(chatId);
                            void state.inputs.delete(chatId);
                        },
                        false,
                        "removeChatMessages",
                    ),
                updateChatMessages: (
                    shouldUpdate: (chatId: ChatId, message: ChatMessage) => boolean,
                    update: (message: ChatMessage) => ChatMessage,
                ): void =>
                    set(
                        (state) => {
                            for (const [chatId, messages] of state.chats.entries()) {
                                for (const [messageId, message] of messages.entries()) {
                                    if (shouldUpdate(chatId, message)) {
                                        const newMessage = update(message);
                                        messages.set(messageId, newMessage);
                                    }
                                }
                            }
                        },
                        false,
                        "updateChatMessages",
                    ),
                inputs: new Map<ChatId, string | undefined>(),
                setInput: (chatId: ChatId, input: string | undefined): void =>
                    set((state) => void state.inputs.set(chatId, input), false, "setInput for chatId: " + chatId),
            }),
            {
                enabled: import.meta.env.VITE_ENV_TYPE !== "production",
                store: "chat-messages",
                name: "workspace/chat-messages",
                serialize: {
                    options: {
                        map: true,
                    },
                },
            },
        ),
    ),
);
